/**
 * Words for scheduled overtime and overtime review (Report 70). The server decides everything; these only say it.
 */
import { formatClock, formatDateRange, formatDay, formatMinutes } from "./myHrText";

const WEEKDAY = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** [1,2,3,4,5] → "Mon–Fri"; [1,3,5] → "Mon, Wed, Fri". */
export function describeWeekdays(weekdays = []) {
  const days = [...new Set(weekdays.map(Number))].filter((day) => day >= 1 && day <= 7).sort((a, b) => a - b);
  if (!days.length) return "No days";
  const consecutive = days.length >= 3 && days.every((day, index) => index === 0 || day === days[index - 1] + 1);
  return consecutive ? `${WEEKDAY[days[0]]}–${WEEKDAY[days[days.length - 1]]}` : days.map((day) => WEEKDAY[day]).join(", ");
}

/** "Mon–Fri · 6:00 PM – 8:00 PM (2 hr a day)". */
export function describeScheduledInterval(item) {
  if (!item) return "";
  const breakText = item.breakMinutes ? `, ${formatMinutes(item.breakMinutes)} unpaid break` : "";
  const nextDay = item.endsNextDay ? " (next day)" : "";
  return `${describeWeekdays(item.weekdays)} · ${formatClock(item.startTime)} – ${formatClock(item.endTime)}${nextDay} (${formatMinutes(item.minutesPerDay)} a day${breakText})`;
}

/** The period it applies, and where an approved one ends early. */
export function describeScheduledPeriod(item) {
  if (!item) return "";
  const range = formatDateRange(item.effectiveFrom, item.effectiveUntil);
  if (item.revokedFrom) return `${range} · ended from ${formatDay(item.revokedFrom, { year: true })}`;
  return range;
}

/** The state as a word the employee and HR both understand. */
export const SCHEDULED_STATE_LABEL = {
  pending: "Waiting for approval",
  upcoming: "Approved, not started yet",
  in_force: "Approved, in force",
  ended: "Ended",
  revoked: "Ended early",
  rejected: "Not approved",
  withdrawn: "Withdrawn",
};

export const REVIEW_KIND_LABEL = {
  UNVERIFIED_OVERTIME: "Approved overtime with no clock-out",
  UNAPPROVED_WORK: "Worked after the shift without approval",
};

export const DISPOSITION_LABEL = {
  CONFIRMED_ZERO: "Not worked",
  NOT_OVERTIME: "Not overtime",
  REFERRED: "Referred",
  // Report 70 final safeguards: linking a correction is not payment; the correction's own stage says whether it is paid.
  SETTLED_BY_CORRECTION: "Linked to overtime pay correction",
};

/** The outcome as a choice in the decision dialog. */
export const DISPOSITION_CHOICE = { ...DISPOSITION_LABEL, SETTLED_BY_CORRECTION: "Link to overtime pay correction" };

/** What each disposition means, shown next to the choice (no legal claim). */
export const DISPOSITION_HINT = {
  CONFIRMED_ZERO: "The employee did not work this overtime. It is not paid; the clock-in and the approval stay on record.",
  NOT_OVERTIME: "The extra time was not work for the business (say what that relies on). It is not paid; the clock times stay on record.",
  REFERRED: "Still being checked. It no longer holds payroll and is not paid yet; it stays under \"Referred · follow up\" until you record the outcome. If the time turns out to be owed after payroll went to Finance, pay it with a pay correction and record that here.",
  SETTLED_BY_CORRECTION: "Links the pay correction that pays what was owed (Payroll › Corrections): an underpayment of overtime minutes on this period's payslip. Linking is not payment: the day stays under \"Referred · follow up\" until Finance records that correction as paid; the clock times and the referral stay on record.",
};

/** Where the payment of a settled referral stands (from the correction itself; approved is not paid). */
export const CORRECTION_STAGE_LABEL = {
  waiting_for_finance: "waiting for Finance",
  approved_unpaid: "approved by Finance, not paid yet",
  paid: "paid",
  void: "cancelled or reversed: record the outcome again",
};

/** One line for a settled referral: correction, minutes and where its payment stands. */
export function correctionLine(review) {
  const correction = review?.correction;
  if (!correction) return "";
  return `Pay correction ${correction.number || ""}${correction.settledMinutes ? ` for ${correction.settledMinutes} min` : ""}: ${CORRECTION_STAGE_LABEL[correction.stage] || correction.stage || ""}`.trim();
}

/** What happens next to a referred day, for the reviewer. */
export const REFERRAL_NEXT_STEP = "Not paid yet and no longer holding payroll. When the check ends, record the outcome: not worked, not overtime, or linked to the overtime pay correction that pays it.";

/** The employee's view of a review item: where it stands, never more than they need. */
export function employeeReviewLine(item) {
  const review = item?.review;
  if (!review) return "Waiting for HR. Not paid yet. If your clock-out is missing, request a correction.";
  if (review.disposition === "REFERRED") return `HR is still checking this: ${review.reason}. Not paid yet; if it is owed, it is paid later by a pay correction.`;
  if (review.disposition === "SETTLED_BY_CORRECTION") {
    const stage = review.correction?.stage;
    if (stage === "paid") return `Paid by a pay correction${review.correction?.number ? ` (${review.correction.number})` : ""}: ${review.reason}`;
    if (stage === "void") return "HR is still checking this. Not paid yet.";
    return `Owed and being paid by a pay correction (${stage === "approved_unpaid" ? "approved, not paid yet" : "waiting for Finance"}): ${review.reason}`;
  }
  return `${DISPOSITION_LABEL[review.disposition] || review.disposition}: ${review.reason}`;
}

/** One line for a review item: "Mon, Oct 12 · 2 hr · Approved overtime with no clock-out". */
export function describeReviewItem(item) {
  if (!item) return "";
  return `${formatDay(item.date)} · ${formatMinutes(item.minutes)} · ${REVIEW_KIND_LABEL[item.kind] || item.kind}`;
}

/** What to do next about an open item, in the reviewer's words. */
export function reviewNextStep(item) {
  if (item?.kind === "UNVERIFIED_OVERTIME") {
    return "Correct the clock-out in Records (the time actually worked is then paid), or record what happened.";
  }
  return "Approve it as overtime (Assign overtime for that day), or record why it is not overtime or refer it.";
}

/** The schedule-fit verdict in a word. */
export const FIT_VERDICT_LABEL = { ok: "Work days match the pay basis", notice: "Worth a look", review: "Check the agreement" };

/** Scheduled overtime that is approved and not over: started or starting later. */
export const isLiveScheduled = (item) => item?.state === "in_force" || item?.state === "upcoming";
